import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import "react-datepicker/dist/react-datepicker.css";
import {
  File,
  User,
  UserCheck,
} from "feather-icons-react/build/IconComponents";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Table } from "antd";

import {
  getAllJobs,
  getPaidPayments,
  getPendingJobs,
  getPendingPayments,
  getAllCounts,
  getMonthlyCounts,
  getDonutChartData,
  getSalesAndCashflow,
  getExpensesBreakdown,
  getOutstandingInvoices,
} from "../../service/dashboardSerice";
import ReactApexChart from "react-apexcharts";

const Dashboard = () => {
  const [pendingJobs, setPendingJobs] = useState(null);
  const [allJobs, setAllJobs] = useState(null);
  const [pendingPayments, setPendingPayments] = useState(null);
  const [paidPayments, setPaidPayments] = useState(null);
  const [counts, setCounts] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [salesCashflow, setSalesCashflow] = useState({
    sales: Array(12).fill(0),
    cashflow: Array(12).fill(0),
  });
  const [expenseBreakdown, setExpenseBreakdown] = useState({ labels: [], series: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [donutData, setDonutData] = useState([0, 0, 0, 0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      loadDashboardData();
    } else {
      setError('User not authenticated');
      setLoading(false);
    }
  }, [selectedYear]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        dashboardData(),
        countData(),
        monthlySalesPurchase(selectedYear),
        fetchOutstandingInvoices(),
        fetchDonutChartData(),
        fetchSalesCashflow(selectedYear),
        fetchExpensesBreakdown(selectedYear)
      ]);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const countData = async () => {
    try {
      const countResponse = await getAllCounts();
      setCounts(countResponse?.data);
    } catch (error) {
      console.log(error.message);
    }
  };

  const monthlySalesPurchase = async (year) => {
    try {
      const response = await getMonthlyCounts(year);
      const paymentData = Array(12).fill(0);

      Object.entries(response.data.payments || {}).forEach(([month, amount]) => {
        paymentData[month - 1] = amount;
      });

      setMonthlySales(paymentData);
    } catch (error) {
      console.log(error.message);
    }
  };

  const dashboardData = async () => {
    setPendingJobs(await getPendingJobs());
    setAllJobs(await getAllJobs());
    setPendingPayments(await getPendingPayments());
    setPaidPayments(await getPaidPayments());
  };

  const fetchOutstandingInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const response = await getOutstandingInvoices(10);
      setOutstandingInvoices(response.data || []);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchSalesCashflow = async (year) => {
    try {
      const response = await getSalesAndCashflow(year);
      setSalesCashflow({
        sales: response.data?.sales || Array(12).fill(0),
        cashflow: response.data?.cashflow || Array(12).fill(0),
      });
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchExpensesBreakdown = async (year) => {
    try {
      const response = await getExpensesBreakdown(year);
      setExpenseBreakdown({
        labels: response.data?.labels || [],
        series: response.data?.series || [],
      });
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchDonutChartData = async () => {
    try {
      const response = await getDonutChartData(selectedYear, selectedMonth);
      setDonutData(response.data?.series || [0, 0, 0, 0]);
    } catch (error) {
      console.log(error.message);
    }
  };

  var donutChart = {
    chart: {
      height: 350,
      type: "donut",
      toolbar: {
        show: false,
      },
    },
    labels: ['Paid', 'Unpaid', 'Partially Paid', 'Cancelled'],
    series: donutData,
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };

  const chartOptions = {
    series: [
      {
        name: "Payment Collection",
        data: monthlySales,
      },
    ],
    colors: ["#28C76F"],
    chart: {
      type: "bar",
      height: 350,
      zoom: {
        enabled: true,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        columnWidth: "50%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ],
    },
    yaxis: {
      labels: {
        formatter: (val) => "LKR " + val.toLocaleString(),
      },
    },
    legend: {
      show: false,
    },
    fill: {
      opacity: 1,
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
            offsetY: 0,
          },
        },
      },
    ],
  };

  const monthCategories = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const salesCashflowChart = {
    series: [
      { name: "Sales (Invoiced)", type: "column", data: salesCashflow.sales },
      { name: "Cashflow (Net)", type: "line", data: salesCashflow.cashflow },
    ],
    colors: ["#7638ff", "#28C76F"],
    chart: {
      height: 350,
      type: "line",
      toolbar: { show: false },
    },
    stroke: { width: [0, 3], curve: "smooth" },
    plotOptions: {
      bar: { columnWidth: "50%", borderRadius: 4 },
    },
    dataLabels: { enabled: false },
    xaxis: { categories: monthCategories },
    yaxis: {
      labels: {
        formatter: (val) => "LKR " + Number(val).toLocaleString(),
      },
    },
    legend: { position: "top" },
    fill: { opacity: 1 },
  };

  const expenseDonutChart = {
    chart: {
      height: 350,
      type: "donut",
      toolbar: { show: false },
    },
    labels: expenseBreakdown.labels,
    series: expenseBreakdown.series,
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
    noData: { text: "No expenses found for this period" },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { width: 200 },
          legend: { position: "bottom" },
        },
      },
    ],
  };

  const invoiceColumns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Customer Name',
      dataIndex: ['customer', 'customerName'],
      key: 'customer.customerName',
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (due_date) => due_date ? new Date(due_date).toLocaleDateString() : "N/A"
    },
    {
      title: 'Total Amount',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (text) => text?.toFixed(2),
    },
    {
      title: 'Amount Due',
      dataIndex: 'balance',
      key: 'balance',
      align: 'right',
      render: (text) => text?.toFixed(2),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
         if (status === "not-paid") {
          return <span className="badge bg-danger">Unpaid</span>
        } else if (status === 'partially-paid') {
          return <span className="badge bg-warning">Partially Paid</span>
        } else {
          return <span className="badge bg-warning">cancelled</span>
        }
      }
    }
  ];

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-12 text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                <h4 className="alert-heading">Error Loading Dashboard</h4>
                <p>{error}</p>
                <hr />
                <button 
                  className="btn btn-primary" 
                  onClick={loadDashboardData}
                >
                  Retry Loading Data
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath
                      src="assets/img/icons/dash1.svg"
                      alt="img"
                    />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp
                      start={0}
                      end={pendingPayments?.totalInvoiceValue}
                      duration={3}
                      prefix="LKR "
                    />
                  </h5>
                  <h6>Total Outstanding</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget dash1 w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath
                      src="assets/img/icons/dash2.svg"
                      alt="img"
                    />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp
                      start={0}
                      end={paidPayments?.totalInvoiceValue}
                      duration={3}
                      prefix="LKR "
                    />
                  </h5>
                  <h6>Total Paid Amount</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget dash2 w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath
                      src="assets/img/icons/dash3.svg"
                      alt="img"
                    />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp
                      start={0}
                      end={pendingJobs?.count}
                      duration={3}
                    />
                  </h5>
                  <h6>Pending Jobs</h6>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-widget dash3 w-100">
                <div className="dash-widgetimg">
                  <span>
                    <ImageWithBasePath
                      src="assets/img/icons/dash4.svg"
                      alt="img"
                    />
                  </span>
                </div>
                <div className="dash-widgetcontent">
                  <h5>
                    <CountUp
                      start={0}
                      end={allJobs?.count}
                      duration={3}
                    />
                  </h5>
                  <h6>All Jobs</h6>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <Link to={"/customers"} className="col-xl-3 col-sm-6 col-12 d-flex">
              <div className="dash-count w-100">
                <div className="dash-counts">
                  <h4>{counts?.customerCount}</h4>
                  <h5>Customers</h5>
                </div>
                <div className="dash-imgs">
                  <User />
                </div>
              </div>
            </Link>
            
            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <Link to ={"/suppliers"} className="w-100 text-decoration-none">
                <div className="dash-count das1 w-100">
                  <div className="dash-counts">
                    <h4>{counts?.supplierCount}</h4>
                    <h5>Suppliers</h5>
                  </div>
                  <div className="dash-imgs">
                    <UserCheck />
                  </div>
                </div>
              </Link>
            </div>

            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <Link
                to={"/employees-grid"}
                className="w-100 text-decoration-none"
              >
                <div className="dash-count das2 w-100">
                  <div className="dash-counts">
                    <h4>{counts?.employeeCount}</h4>
                    <h5>Employee</h5>
                  </div>
                  <div className="dash-imgs">
                    <File />
                  </div>
                </div>
              </Link>
            </div>

            <div className="col-xl-3 col-sm-6 col-12 d-flex">
              <Link
                to={"/invoices-list"}
                className="w-100 text-decoration-none"
              >
                <div className="dash-count das1 w-100">
                  <div className="dash-counts">
                    <h4>{counts?.InvoiceCount}</h4>
                    <h5>Invoice</h5>
                  </div>
                  <div className="dash-imgs">
                    <File />
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <div className="row">
            <div className="col-xl-6 col-sm-12 col-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Payment Collection</h5>
                  <div className="graph-sets">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {[
                        new Date().getFullYear(),
                        new Date().getFullYear() - 1,
                        new Date().getFullYear() - 2,
                        new Date().getFullYear() - 3,
                      ].map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  <div id="sales_charts" />
                  <Chart
                    options={chartOptions}
                    series={chartOptions.series}
                    type="bar"
                    height={320}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title">Donut Chart</h5>
                  <div className="d-flex gap-2">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    >
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                        <option key={i} value={i + 1}>{m}</option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {[
                        new Date().getFullYear(),
                        new Date().getFullYear() - 1,
                        new Date().getFullYear() - 2,
                        new Date().getFullYear() - 3,
                      ].map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  <div id="donut-chart" />
                  <ReactApexChart
                    options={donutChart}
                    series={donutChart.series}
                    type="donut"
                    height={350}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-xl-6 col-sm-12 col-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Sales &amp; Cashflow</h5>
                  <div className="graph-sets">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                      {[
                        new Date().getFullYear(),
                        new Date().getFullYear() - 1,
                        new Date().getFullYear() - 2,
                        new Date().getFullYear() - 3,
                      ].map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="card-body">
                  <ReactApexChart
                    options={salesCashflowChart}
                    series={salesCashflowChart.series}
                    type="line"
                    height={320}
                  />
                </div>
              </div>
            </div>
            <div className="col-xl-6 col-sm-12 col-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Expenses Breakdown</h5>
                </div>
                <div className="card-body">
                  {expenseBreakdown.series.length > 0 ? (
                    <ReactApexChart
                      options={expenseDonutChart}
                      series={expenseDonutChart.series}
                      type="donut"
                      height={350}
                    />
                  ) : (
                    <div className="text-center text-muted py-5">
                      No expenses found for {selectedYear}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Outstanding Invoices (Top 10 by Balance)</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <Table
                      columns={invoiceColumns}
                      dataSource={outstandingInvoices}
                      rowKey="_id"
                      pagination={false}
                      loading={loadingInvoices}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;